import type React from "react"
import { useTranslation } from "react-i18next"
import { useState, useEffect, useRef } from "react"
import Hls from "hls.js"
import { Box, Typography, CircularProgress, IconButton, Tooltip, Paper } from "@mui/material"
import {
  Fullscreen as FullscreenIcon,
  VolumeUp as VolumeUpIcon,
  VolumeOff as VolumeOffIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material"
import { useIsMobile } from "../hooks/useMediaQuery"

interface StreamPlayerProps {
  streamUrl: string
  cameraName?: string
  autoPlay?: boolean
  width?: string | number
  height?: string | number
  onError?: (error: any) => void
  showControls?: boolean
  onRetry?: () => void
}

const StreamPlayer: React.FC<StreamPlayerProps> = ({
  streamUrl,
  cameraName = "",
  autoPlay = true,
  width = "100%",
  height = 400,
  onError,
  showControls = true,
  onRetry,
}) => {
  const { t } = useTranslation()
  const videoRef = useRef<HTMLVideoElement>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [muted, setMuted] = useState<boolean>(true)
  const [hlsInstance, setHlsInstance] = useState<Hls | null>(null)
  const [retryCount, setRetryCount] = useState<number>(0)
  const isMobile = useIsMobile()
  const maxRetries = 3

  useEffect(() => {
    let hls: Hls | null = null

    const setupHls = () => {
      setLoading(true)
      setError(null)

      // Limpar qualquer instância anterior do HLS
      if (hlsInstance) {
        hlsInstance.destroy()
        setHlsInstance(null)
      }

      const videoElement = videoRef.current
      if (!videoElement) return

      if (!streamUrl) {
        setError(t("stream.noUrl"))
        setLoading(false)
        return
      }

      console.log(`Tentando carregar stream HLS (tentativa ${retryCount + 1}):`, streamUrl)

      // Verificar se o navegador suporta HLS nativamente
      if (videoElement.canPlayType("application/vnd.apple.mpegurl")) {
        // Navegadores que suportam HLS nativamente (Safari)
        videoElement.src = streamUrl
        videoElement.addEventListener("loadedmetadata", () => {
          console.log("Stream HLS carregado (nativo)")
          if (autoPlay) videoElement.play().catch((e) => console.error("Erro ao iniciar reprodução:", e))
          setLoading(false)
        })
        videoElement.addEventListener("error", (e) => {
          console.error("Erro no elemento de vídeo:", e)
          setError(t("stream.loadError"))
          setLoading(false)
          if (onError) onError(e)
        })
      } else if (Hls.isSupported()) {
        // Navegadores que precisam do hls.js (Chrome, Firefox, etc)
        hls = new Hls({
          enableWorker: true,
          lowLatencyMode: true,
          backBufferLength: 90,
          maxBufferLength: 30,
        })

        hls.loadSource(streamUrl)
        hls.attachMedia(videoElement)

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          console.log("Stream HLS carregado (hls.js)")
          if (autoPlay) videoElement.play().catch((e) => console.error("Erro ao iniciar reprodução:", e))
          setLoading(false)
        })

        hls.on(Hls.Events.ERROR, (event, data) => {
          console.error("Erro no hls.js:", data)

          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                console.log("Erro de rede, tentando recuperar...")
                hls?.startLoad()
                break
              case Hls.ErrorTypes.MEDIA_ERROR:
                console.log("Erro de mídia, tentando recuperar...")
                hls?.recoverMediaError()
                break
              default:
                // Erro fatal não recuperável
                if (retryCount < maxRetries) {
                  console.log(`Tentando novamente (${retryCount + 1}/${maxRetries})...`)
                  setRetryCount((prev) => prev + 1)
                  handleRetry()
                } else {
                  console.error("Número máximo de tentativas excedido")
                  hls?.destroy()
                  setError(t("stream.loadStreamError", { details: data.details }))
                  setLoading(false)
                  if (onError) onError(data)
                }
                break
            }
          }
        })

        setHlsInstance(hls)
      } else {
        setError(t("stream.noHlsPlaybackSupport"))
        setLoading(false)
        if (onError) onError(new Error(t("stream.hlsNotSupported")))
      }
    }

    setupHls()

    // Cleanup
    return () => {
      if (hls) {
        hls.destroy()
      }
    }
  }, [streamUrl, autoPlay, retryCount])

  // Função para tentar novamente
  const handleRetry = () => {
    setRetryCount((prev) => prev + 1)
    if (onRetry) onRetry()
  }

  // Função para tela cheia
  const toggleFullscreen = () => {
    if (videoRef.current) {
      if (videoRef.current.requestFullscreen) {
        videoRef.current.requestFullscreen()
      }
    }
  }

  // Função para alternar mudo
  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !muted
      setMuted(!muted)
    }
  }

  // Ajustar altura para dispositivos móveis
  const responsiveHeight = isMobile ? 250 : height

  return (
    <Paper
      elevation={3}
      sx={{
        position: "relative",
        overflow: "hidden",
        bgcolor: "black",
        borderRadius: 1,
        width: width,
        height: responsiveHeight,
      }}
    >
      {loading && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(0,0,0,0.7)",
            zIndex: 10,
          }}
        >
          <CircularProgress color="primary" sx={{ mb: 2 }} />
          <Typography color="white" variant="body2">
            {t("stream.loading")}
          </Typography>
        </Box>
      )}

      {error && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(0,0,0,0.7)",
            zIndex: 10,
            p: 2,
          }}
        >
          <WarningIcon sx={{ fontSize: 60, color: "error.main", mb: 2 }} />
          <Typography color="error" variant="h6" align="center" gutterBottom>
            {t("stream.loadStreamErrorTitle")}
          </Typography>
          <Typography color="white" variant="body2" align="center" sx={{ mb: 3 }}>
            {error}
          </Typography>

          <Box>
            <Tooltip title={t("stream.retry")}>
              <IconButton color="primary" onClick={handleRetry} size="large">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      )}

      <video
        ref={videoRef}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "contain",
          backgroundColor: "#000",
        }}
        muted={muted}
        playsInline
        controls={showControls}
      />

      {!showControls && !error && !loading && (
        <Box
          sx={{
            position: "absolute",
            bottom: 16,
            left: 16,
            right: 16,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            zIndex: 5,
            opacity: 0.8,
            "&:hover": {
              opacity: 1,
            },
          }}
        >
          <Typography variant="body2" color="white" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
            {cameraName || t("stream.defaultCamera")}
          </Typography>

          <Box>
            <Tooltip title={muted ? t("stream.unmute") : t("stream.mute")}>
              <IconButton onClick={toggleMute} size={isMobile ? "small" : "medium"} sx={{ color: "white", mr: 1 }}>
                {muted ? <VolumeOffIcon /> : <VolumeUpIcon />}
              </IconButton>
            </Tooltip>

            <Tooltip title={t("stream.fullscreen")}>
              <IconButton onClick={toggleFullscreen} size={isMobile ? "small" : "medium"} sx={{ color: "white" }}>
                <FullscreenIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      )}
    </Paper>
  )
}

export default StreamPlayer